/**
 * Phase H (H.2) — Version History dialog.
 *
 * Reads `documentVersions` rows for the given document (Q11 locked:
 * read the existing `documentVersions` Convex table; no Graph API call
 * for now). Renders a Win95-styled modal that lists versions
 * newest-first; click a version to expand a textual diff between
 * itself and the version immediately older.
 *
 * Diff: re-uses `computeLineDiff` from `@repancy/core` so we don't
 * ship a second diff algorithm. The same engine powers `UnifiedDiff`
 * in the timeline window — visual style is intentionally similar for
 * lawyer familiarity.
 */

import { useState, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { computeLineDiff } from '@repancy/core';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { testIds } from '../../lib/testIds';

interface VersionHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentTitle: string;
}

interface VersionRow {
  _id: string;
  versionNumber: number;
  status: string;
  content: string;
  createdAt: string;
  createdBy: string;
  changeDescription: string;
}

const FONT = "var(--xp-font-body)";

function looksLikeConvexId(id: string): boolean {
  return id.length > 8 && !/^d\d+$/.test(id);
}

export function VersionHistoryDialog({
  open,
  onClose,
  documentId,
  documentTitle,
}: VersionHistoryDialogProps) {
  const isRealId = looksLikeConvexId(documentId);

  // Pull live versions only — `documentVersions` is the canonical Convex
  // table (Q11 lock).
  const liveVersions = useQuery(
    api.documentVersions.getVersions,
    isRealId && open ? { documentId: documentId as Id<'documents'> } : 'skip',
  );

  const versions: VersionRow[] = useMemo(() => {
    if (!liveVersions) return [];
    return [...liveVersions]
      .map((v) => ({
        _id: String(v._id),
        versionNumber: v.versionNumber,
        status: v.status,
        content: v.content,
        createdAt: v.createdAt,
        createdBy: v.createdBy,
        changeDescription: v.changeDescription,
      }))
      .sort((a, b) => b.versionNumber - a.versionNumber);
  }, [liveVersions]);

  const [expandedVersion, setExpandedVersion] = useState<number | null>(null);

  if (!open) return null;

  const emptyReason: string | null =
    !isRealId
      ? 'Version history is available once this document is saved to Convex. Fixture documents (d1, d2, …) do not maintain a version log.'
      : liveVersions === undefined
        ? null // loading
        : versions.length === 0
          ? 'No prior versions yet. The next save will create version 2.'
          : null;

  return (
    <div
      style={overlayStyle}
      onClick={onClose}
      data-testid={testIds.versionHistory.overlay}
    >
      <div
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
        data-testid={testIds.versionHistory.root}
      >
        <div style={titleBarStyle}>
          <span>Version History — {documentTitle}</span>
          <CloseBtn onClick={onClose} />
        </div>

        <div style={bodyStyle}>
          {emptyReason !== null && (
            <p
              data-testid={testIds.versionHistory.emptyState}
              style={{ fontSize: 12, lineHeight: 1.5, padding: 6, color: '#000' }}
            >
              {emptyReason}
            </p>
          )}
          {emptyReason === null && liveVersions === undefined && (
            <p style={{ fontSize: 12, padding: 6, color: '#000' }}>Loading versions…</p>
          )}
          {emptyReason === null && versions.length > 0 && (
            <ul
              style={{ listStyle: 'none', margin: 0, padding: 0 }}
              data-testid={testIds.versionHistory.list}
            >
              {versions.map((v, i) => {
                const olderVersion = versions[i + 1] ?? null;
                const expanded = expandedVersion === v.versionNumber;
                return (
                  <li
                    key={v._id}
                    style={rowStyle}
                    data-testid={testIds.versionHistory.row(v.versionNumber)}
                  >
                    <button
                      style={rowHeaderStyle(expanded)}
                      onClick={() =>
                        setExpandedVersion((prev) =>
                          prev === v.versionNumber ? null : v.versionNumber,
                        )
                      }
                      data-testid={testIds.versionHistory.toggle(v.versionNumber)}
                    >
                      <span style={{ fontWeight: 'bold', marginRight: 8 }}>
                        v{v.versionNumber}
                      </span>
                      <span style={{ marginRight: 8, color: '#000' }}>{v.status}</span>
                      <span style={{ flex: 1, color: '#000' }}>
                        {v.changeDescription || '(no description)'}
                      </span>
                      <span style={{ fontSize: 10, color: '#000' }}>
                        {new Date(v.createdAt).toLocaleString()}
                      </span>
                      <span style={{ marginLeft: 8 }}>{expanded ? '▼' : '▶'}</span>
                    </button>
                    {expanded && olderVersion === null && (
                      <div
                        style={diffContainerStyle}
                        data-testid={testIds.versionHistory.diff(v.versionNumber)}
                      >
                        <p style={{ padding: 8, color: '#000', fontSize: 11 }}>
                          Earliest version — nothing to diff against.
                        </p>
                      </div>
                    )}
                    {expanded && olderVersion !== null && (
                      <div
                        style={diffContainerStyle}
                        data-testid={testIds.versionHistory.diff(v.versionNumber)}
                      >
                        <DiffView olderText={olderVersion.content} newerText={v.content} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div style={footerStyle}>
          <DialogButton
            label="Close"
            onClick={onClose}
            data-testid={testIds.versionHistory.closeButton}
          />
        </div>
      </div>
    </div>
  );
}

/** Inline diff using @repancy/core. Caps at 200 visible hunks so very
 *  large diffs don't blow up the DOM. */
function DiffView({ olderText, newerText }: { olderText: string; newerText: string }) {
  const hunks = useMemo(
    () => computeLineDiff(olderText, newerText).slice(0, 200),
    [olderText, newerText],
  );
  return (
    <div style={diffScrollStyle}>
      {hunks.length === 0 && (
        <div style={{ padding: 6, color: '#000', fontSize: 11 }}>
          No textual difference between these versions.
        </div>
      )}
      {hunks.map((h, i) => (
        <div
          key={`${h.type}-${h.lineNumber}-${i}`}
          style={hunkRowStyle(h.type)}
          data-testid={testIds.versionHistory.diffLine(h.type)}
        >
          <span style={lineNumStyle}>{h.lineNumber}</span>
          <span style={lineMarkerStyle}>
            {h.type === 'added' ? '+' : h.type === 'removed' ? '−' : ' '}
          </span>
          <span style={lineContentStyle}>{h.content}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Win95 chrome helpers
// ---------------------------------------------------------------------------

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
};

const dialogStyle: React.CSSProperties = {
  width: 640,
  maxWidth: '92vw',
  maxHeight: '85vh',
  background: '#ece9d8',
  borderStyle: 'solid',
  borderWidth: 2,
  boxShadow: '3px 3px 0 #000',
  fontFamily: FONT,
  fontSize: 12,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const titleBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: 'linear-gradient(90deg, var(--xp-selection), #1a4ab8)',
  padding: '3px 4px',
  color: 'white',
  fontWeight: 'bold',
  fontSize: 13,
  userSelect: 'none',
  flexShrink: 0,
};

const bodyStyle: React.CSSProperties = {
  padding: 6,
  overflowY: 'auto',
  flex: 1,
  background: 'white',
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 6,
  padding: '8px 12px',
  borderTop: '1px solid #808080',
  flexShrink: 0,
};

const rowStyle: React.CSSProperties = {
  borderBottom: '1px solid #d9e8fa',
};

function rowHeaderStyle(expanded: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '6px 8px',
    background: expanded ? '#d9e8fa' : 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: FONT,
    fontSize: 11,
    color: '#000',
    textAlign: 'left',
  };
}

const diffContainerStyle: React.CSSProperties = {
  background: '#fafafa',
  borderTop: '1px solid #808080',
  borderBottom: '1px solid #808080',
};

const diffScrollStyle: React.CSSProperties = {
  maxHeight: 320,
  overflow: 'auto',
  padding: 4,
  fontFamily: "var(--xp-font-mono)",
  fontSize: 11,
};

function hunkRowStyle(type: 'added' | 'removed' | 'unchanged'): React.CSSProperties {
  const bg =
    type === 'added' ? '#dcffdc' : type === 'removed' ? '#ffe0e0' : 'transparent';
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
    padding: '1px 4px',
    background: bg,
    whiteSpace: 'pre-wrap',
  };
}

const lineNumStyle: React.CSSProperties = {
  width: 36,
  textAlign: 'right',
  color: '#000',
  paddingRight: 4,
  borderRight: '1px solid #d0d0d0',
  userSelect: 'none',
};

const lineMarkerStyle: React.CSSProperties = {
  width: 14,
  textAlign: 'center',
  color: '#000',
  userSelect: 'none',
};

const lineContentStyle: React.CSSProperties = {
  flex: 1,
  color: '#000',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

function CloseBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      aria-label="Close"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseDown={(e) => { e.stopPropagation(); }}
      style={{
        width: 20,
        height: 18,
        background: '#ece9d8',
        borderStyle: 'solid',
        borderWidth: 2,
        color: '#000',
        fontSize: 12,
        fontWeight: 'bold',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        lineHeight: 1,
      }}
    >
      X
    </button>
  );
}

function DialogButton({
  label,
  onClick,
  'data-testid': dataTestId,
}: {
  label: string;
  onClick: () => void;
  'data-testid'?: string;
}) {
  return (
    <button
      onClick={onClick}
      data-testid={dataTestId}
      style={{
        minWidth: 75,
        height: 26,
        padding: '0 16px',
        background: '#ece9d8',
        borderStyle: 'solid',
        borderWidth: 2,
        fontFamily: FONT,
        fontSize: 12,
        cursor: 'pointer',
        color: '#000',
      }}
    >
      {label}
    </button>
  );
}
